import { useEffect, useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Empty,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CustomerReceipt, ReceiptAssignmentType } from '@goelectric/shared-types';
import { useReceipts } from '../hooks/useReceipts';
import { describeAssignmentError } from '../utils/errors';
import { formatBytes, formatDateTime } from '../utils/format';
import {
  CUSTOMER_RECEIPT_ACCEPT,
  receiptMimeLabel,
  validateCustomerReceipt,
  type CustomerReceiptValidation,
} from '../utils/receipts';

/** Asignación sobre la que se abren los comprobantes. */
export interface ReceiptsTarget {
  type: ReceiptAssignmentType;
  assignmentId: string;
  /** Texto visible de la asignación (pack/membresía) para el título del modal. */
  label: string;
}

export interface ReceiptsModalProps {
  open: boolean;
  customerId: string;
  target: ReceiptsTarget | null;
  /** La subida es solo `SUPER_ADMIN`; el resto ve los metadatos en modo consulta. */
  canWrite: boolean;
  onClose: () => void;
}

/**
 * Modal de **Comprobantes de pago** de una asignación (US-2.4, TG-53).
 *
 * Lista metadatos (sin `storageKey`), permite subir un PDF/JPG/PNG por
 * `multipart/form-data` (campo `file`) y descargar por la `downloadUrl` firmada que
 * entrega el detalle del comprobante. Traduce los errores del backend de formato,
 * tamaño (`413`) y duplicado (`409`).
 */
export function ReceiptsModal({
  open,
  customerId,
  target,
  canWrite,
  onClose,
}: ReceiptsModalProps) {
  const { message } = App.useApp();
  const receipts = useReceipts(
    customerId,
    target?.type ?? 'pack',
    target?.assignmentId,
    open && Boolean(target),
  );
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<CustomerReceiptValidation | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  /** URL firmada cuando el navegador bloqueó la ventana abierta de forma síncrona. */
  const [downloadFallbackUrl, setDownloadFallbackUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setValidation(null);
      setUploadError(null);
      setActionError(null);
      setDownloadingId(null);
      setDownloadFallbackUrl(null);
    }
  }, [open, target?.assignmentId]);

  const beforeUpload: UploadProps['beforeUpload'] = (candidate) => {
    const typed = candidate as File;
    setFile(typed);
    setValidation(validateCustomerReceipt(typed));
    setUploadError(null);
    return false;
  };

  const resetFile = () => {
    setFile(null);
    setValidation(null);
    setUploadError(null);
  };

  const submitUpload = async () => {
    if (!file || !validation?.valid) return;
    setUploadError(null);
    try {
      await receipts.upload(file);
      message.success('Comprobante subido.');
      resetFile();
    } catch (caught) {
      setUploadError(describeAssignmentError(caught, 'No se pudo subir el comprobante.'));
    }
  };

  const download = async (receipt: CustomerReceipt) => {
    setActionError(null);
    setDownloadFallbackUrl(null);
    setDownloadingId(receipt.id);
    /**
     * Abrir la ventana en blanco DENTRO del handler del clic conserva la activación
     * del usuario; resolver la URL firmada es asíncrono y abrir después suele ser
     * bloqueado por el navegador. Se corta el `opener` para no exponer el Admin.
     */
    const popup = window.open('', '_blank');
    if (popup) {
      popup.opener = null;
      try {
        // La ventana en blanco es same-origin: se fija `no-referrer` antes de navegar.
        const meta = popup.document.createElement('meta');
        meta.name = 'referrer';
        meta.content = 'no-referrer';
        popup.document.head.appendChild(meta);
      } catch {
        // Sin acceso al documento (p. ej. stub de test) el opener nulo ya protege.
      }
    }
    try {
      const url = await receipts.resolveDownloadUrl(receipt.id);
      if (!url) {
        throw new Error('La descarga no está disponible en este momento.');
      }
      if (popup) {
        popup.location.href = url;
      } else {
        // Ventana bloqueada: se ofrece un enlace para que el operador reintente.
        setDownloadFallbackUrl(url);
      }
    } catch (caught) {
      if (popup && !popup.closed) popup.close();
      setActionError(describeAssignmentError(caught, 'No se pudo descargar el comprobante.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const columns: ColumnsType<CustomerReceipt> = [
    {
      title: 'Archivo',
      key: 'file',
      render: (_, receipt) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{receipt.originalName}</Typography.Text>
          <Space size={6}>
            <Tag>{receiptMimeLabel(receipt.mimeType)}</Tag>
            <Typography.Text style={{ fontSize: 12 }}>
              {formatBytes(receipt.sizeBytes)}
            </Typography.Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Subido',
      key: 'uploadedAt',
      width: 220,
      render: (_, receipt) => formatDateTime(receipt.uploadedAt),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 140,
      align: 'right',
      render: (_, receipt) => (
        <Button
          size="small"
          loading={downloadingId === receipt.id}
          aria-label={`Descargar ${receipt.originalName}`}
          onClick={() => void download(receipt)}
        >
          Descargar
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={`Comprobantes de pago${target ? ` — ${target.label}` : ''}`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Cerrar</Button>}
      width={760}
      destroyOnHidden
      keyboard
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {actionError && (
          <Alert
            type="error"
            showIcon
            role="alert"
            message="No se pudo completar la descarga"
            description={actionError}
          />
        )}

        {downloadFallbackUrl && (
          <Alert
            type="info"
            showIcon
            message="El navegador bloqueó la ventana de descarga"
            description="Abre el comprobante con el siguiente enlace."
            action={
              <Button
                href={downloadFallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
              >
                Abrir comprobante
              </Button>
            }
          />
        )}

        {canWrite ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text strong>Subir comprobante</Typography.Text>
            <Upload.Dragger
              name="customer-receipt-file"
              accept={CUSTOMER_RECEIPT_ACCEPT}
              maxCount={1}
              showUploadList={false}
              beforeUpload={beforeUpload}
              onRemove={resetFile}
              aria-label="Seleccionar comprobante PDF, JPG o PNG"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Haz clic o arrastra un PDF/JPG/PNG aquí</p>
              <Typography.Text>
                El servidor valida el tamaño máximo, el tipo real del archivo y evita duplicados.
              </Typography.Text>
            </Upload.Dragger>

            {file && (
              <Space wrap>
                <Typography.Text>{file.name}</Typography.Text>
                <Typography.Text>{formatBytes(file.size)}</Typography.Text>
              </Space>
            )}

            {file && validation && !validation.valid && (
              <Alert type="error" showIcon message={validation.error} />
            )}

            {uploadError && (
              <Alert
                type="error"
                showIcon
                role="alert"
                message="No se pudo subir el comprobante"
                description={uploadError}
              />
            )}

            <Space>
              <Button onClick={resetFile} disabled={!file || receipts.uploading}>
                Quitar
              </Button>
              <Button
                type="primary"
                loading={receipts.uploading}
                disabled={!file || !validation?.valid}
                onClick={() => void submitUpload()}
              >
                Subir comprobante
              </Button>
            </Space>
          </Space>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Modo consulta"
            description="Solo un SUPER_ADMIN puede subir comprobantes. Puedes consultarlos y descargarlos."
          />
        )}

        {receipts.error ? (
          <Alert
            type="error"
            showIcon
            message="No se pudieron cargar los comprobantes"
            description={receipts.error}
            action={
              <Button size="small" onClick={receipts.refresh}>
                Reintentar
              </Button>
            }
          />
        ) : (
          <Table<CustomerReceipt>
            rowKey="id"
            size="small"
            loading={receipts.loading}
            columns={columns}
            dataSource={receipts.receipts}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Esta asignación aún no tiene comprobantes."
                />
              ),
            }}
          />
        )}
      </Space>
    </Modal>
  );
}
